import { dcos, dsin, type Rng } from "@eldrun/shared";
import { HEIGHT_SPACING, WORLD_HALF } from "@eldrun/worldgen";

/** Direção do olhar (yaw 0 olha para −z; pitch positivo olha para cima), unitária. */
export function lookDirection(yaw: number, pitch: number): { x: number; y: number; z: number } {
  const cp = dcos(pitch);
  return { x: -dsin(yaw) * cp, y: dsin(pitch), z: -dcos(yaw) * cp };
}

export interface MeleeCandidate {
  id: string;
  x: number;
  z: number;
  /** Faixa vertical do corpo (pés e topo da cabeça). */
  bottom: number;
  top: number;
  /**
   * Raio do corpo (m), quando difere do padrão do cone (criaturas): o alcance conta até a pele e o
   * cone horizontal abre o tanto que o corpo ocupa na vista — o lado de um corpo grande também apanha.
   */
  radius?: number;
}

export interface MeleeCone {
  reach: number;
  bodyRadius: number;
  cosHalfH: number;
  cosHalfV: number;
  sinHalfV: number;
  maxTargets: number;
  /** Fator de cada alvo extra (1, f, f², …). */
  falloff: number;
}

export interface MeleeHit {
  id: string;
  distance: number;
  factor: number;
  /** Ponto do corpo mais perto da linha do olhar (alvo da linha de visão e dos números). */
  point: { x: number; y: number; z: number };
}

/**
 * Alvos do golpe (doc 02 §10): dentro do alcance (+ raio do corpo) e do cone horizontal e
 * vertical a partir do olho, sem nada no caminho (`blocked`), do mais perto ao mais longe, até
 * `maxTargets`, com o fator de multi-alvo.
 */
export function meleeHits(
  eye: { x: number; y: number; z: number },
  yaw: number,
  pitch: number,
  candidates: readonly MeleeCandidate[],
  cone: MeleeCone,
  blocked: (candidate: MeleeCandidate, point: { x: number; y: number; z: number }) => boolean = () => false,
): MeleeHit[] {
  const fx = -dsin(yaw);
  const fz = -dcos(yaw);
  const cp = dcos(pitch);
  const sp = dsin(pitch);
  const inside: { candidate: MeleeCandidate; distance: number; point: { x: number; y: number; z: number } }[] = [];
  const sinHalfH = Math.sqrt(Math.max(0, 1 - cone.cosHalfH * cone.cosHalfH));
  for (const c of candidates) {
    const body = c.radius ?? cone.bodyRadius;
    const range = cone.reach + body;
    const dx = c.x - eye.x;
    const dz = c.z - eye.z;
    const horizontal = Math.sqrt(dx * dx + dz * dz);
    const along = eye.y + (horizontal * sp) / cp;
    if (horizontal <= body) {
      // Colado no atacante: qualquer direção vale (não dá para "fugir" para dentro do corpo).
      const y = along < c.bottom ? c.bottom : along > c.top ? c.top : along;
      inside.push({ candidate: c, distance: Math.sqrt(horizontal * horizontal + (y - eye.y) * (y - eye.y)), point: { x: c.x, y, z: c.z } });
      continue;
    }
    if (horizontal > range) continue;
    // Cone horizontal: no centro do alvo (padrão) ou, com raio próprio, até a borda dele (meia-abertura +
    // o meio-ângulo do corpo visto do olho).
    let cosLimit = cone.cosHalfH;
    if (c.radius !== undefined) {
      const sinBody = Math.min(1, c.radius / horizontal);
      cosLimit = cone.cosHalfH * Math.sqrt(1 - sinBody * sinBody) - sinHalfH * sinBody;
    }
    if ((dx * fx + dz * fz) / horizontal < cosLimit) continue;
    // O corpo é um segmento vertical. Intersecta a faixa dele com o alcance (esfera) e com o cone
    // vertical (elevação entre pitch − meia-abertura e pitch + meia-abertura); se sobrar algo, o
    // ponto mirado é o dessa faixa mais perto da linha do olhar.
    const vertical = Math.sqrt(range * range - horizontal * horizontal);
    let lo = Math.max(c.bottom, eye.y - vertical);
    let hi = Math.min(c.top, eye.y + vertical);
    // tan(pitch ± meia) pela soma de ângulos; denominador ≤ 0 = além da vertical (sem limite).
    const downDen = cp * cone.cosHalfV + sp * cone.sinHalfV;
    const upDen = cp * cone.cosHalfV - sp * cone.sinHalfV;
    if (downDen > 1e-9) lo = Math.max(lo, eye.y + (horizontal * (sp * cone.cosHalfV - cp * cone.sinHalfV)) / downDen);
    if (upDen > 1e-9) hi = Math.min(hi, eye.y + (horizontal * (sp * cone.cosHalfV + cp * cone.sinHalfV)) / upDen);
    if (lo > hi) continue;
    const y = along < lo ? lo : along > hi ? hi : along;
    const dy = y - eye.y;
    inside.push({ candidate: c, distance: Math.sqrt(horizontal * horizontal + dy * dy), point: { x: c.x, y, z: c.z } });
  }
  inside.sort((a, b) => a.distance - b.distance || (a.candidate.id < b.candidate.id ? -1 : 1));
  const hits: MeleeHit[] = [];
  let factor = 1;
  for (const { candidate, distance, point } of inside) {
    if (hits.length >= cone.maxTargets) break;
    if (blocked(candidate, point)) continue;
    hits.push({ id: candidate.id, distance, factor, point });
    factor *= cone.falloff;
  }
  return hits;
}

export interface DamageRoll {
  base: number;
  /** Multiplicadores fixos já conhecidos (multi-alvo, Prata, chefes…). */
  multiplier: number;
  variance: readonly [number, number];
  critChance: number;
  critMul: number;
  /** Algum tipo do golpe é fraqueza do alvo. */
  weak: boolean;
  weakMul: number;
}

export interface RolledDamage {
  amount: number;
  crit: boolean;
  weak: boolean;
}

/** Dano bruto do golpe (doc 02 §11): base × V × crítico × fraqueza × multiplicadores. */
export function rollDamage(roll: DamageRoll, rng: Rng): RolledDamage {
  const v = rng.range(roll.variance[0], roll.variance[1]);
  const crit = rng.chance(roll.critChance);
  const amount = roll.base * v * (crit ? roll.critMul : 1) * (roll.weak ? roll.weakMul : 1) * roll.multiplier;
  return { amount, crit, weak: roll.weak };
}

/**
 * O segmento de `a` a `b` atravessa o terreno? Exato para o heightfield (interpolação por
 * triângulo, diagonal (i, j)–(i+1, j+1)): dentro de um triângulo a diferença entre terreno e
 * segmento é linear, então basta conferir onde o segmento cruza as linhas da grade (x e z
 * constantes) e as diagonais (x − z constante). Amostrar em passo fixo deixava passar picos.
 */
export function terrainBlocks(
  height: (x: number, z: number) => number,
  a: { x: number; y: number; z: number },
  b: { x: number; y: number; z: number },
  spacing: number = HEIGHT_SPACING,
  origin: number = -WORLD_HALF,
): boolean {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const dz = b.z - a.z;
  const blockedAt = (t: number) => t > 0 && t < 1 && height(a.x + dx * t, a.z + dz * t) > a.y + dy * t + 0.05;
  // Linhas x = origem + k·s e z = origem + k·s.
  for (const [p0, d] of [
    [a.x, dx],
    [a.z, dz],
  ] as const) {
    if (Math.abs(d) < 1e-12) continue;
    const k0 = Math.ceil((Math.min(p0, p0 + d) - origin) / spacing);
    const k1 = Math.floor((Math.max(p0, p0 + d) - origin) / spacing);
    for (let k = k0; k <= k1; k++) if (blockedAt((origin + k * spacing - p0) / d)) return true;
  }
  // Diagonais: x − z = m·s (a origem é a mesma nos dois eixos).
  const q0 = a.x - a.z;
  const dq = dx - dz;
  if (Math.abs(dq) > 1e-12) {
    const m0 = Math.ceil(Math.min(q0, q0 + dq) / spacing);
    const m1 = Math.floor(Math.max(q0, q0 + dq) / spacing);
    for (let m = m0; m <= m1; m++) if (blockedAt((m * spacing - q0) / dq)) return true;
  }
  return false;
}

/** O segmento cruza o cilindro vertical (no plano e na faixa de altura)? */
export function segmentHitsCylinder(
  a: { x: number; y: number; z: number },
  b: { x: number; y: number; z: number },
  c: { x: number; z: number; radius: number; bottom: number; top: number },
): boolean {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const fx = a.x - c.x;
  const fz = a.z - c.z;
  const qa = dx * dx + dz * dz;
  const qb = 2 * (fx * dx + fz * dz);
  const qc = fx * fx + fz * fz - c.radius * c.radius;
  let t0 = 0;
  let t1 = 1;
  if (qa < 1e-12) {
    if (qc > 0) return false;
  } else {
    const disc = qb * qb - 4 * qa * qc;
    if (disc < 0) return false;
    const root = Math.sqrt(disc);
    t0 = Math.max(0, (-qb - root) / (2 * qa));
    t1 = Math.min(1, (-qb + root) / (2 * qa));
    if (t0 > t1) return false;
  }
  // Dentro do círculo entre t0 e t1: confere a altura nesse trecho.
  const y0 = a.y + (b.y - a.y) * t0;
  const y1 = a.y + (b.y - a.y) * t1;
  return Math.max(y0, y1) >= c.bottom && Math.min(y0, y1) <= c.top;
}
